import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { Button, ConflictDialog, Drawer } from './common'
import { HistoryPanel } from './HistoryPanel'
import { api, conflictCurrent } from '../lib/api'
import { compressImageToWebp, IMAGE_LIMITS, type CompressedImage } from '../lib/image-compression'
import { enforceWorkingItemCompletion } from '../lib/working-item-rules'
import type { DashboardData, WorkingAttachment, WorkingItem } from '../lib/types'
import { WorkingFormView, emptyWorkingForm, toWorkingForm, type WorkingForm } from '../pages/WorkingItemsPage'

interface PendingAttachment extends CompressedImage {
  id: string
  previewUrl: string
}

const conflictLabels = {
  projectName: 'Project',
  task: '工作項目',
  owner: 'Owner',
  startDate: '開始日期',
  dueDate: '到期日期',
  status: '狀態',
  done: '完成',
  isPriority: '重點項目',
  subtasks: '工作細項',
  links: '參考連結',
  readinessItemIds: '關聯 Test Item',
  notes: '備註',
}

export function WorkingItemEditorDrawer({
  open,
  item,
  data,
  onClose,
  onMessage,
}: {
  open: boolean
  item: WorkingItem | null
  data: DashboardData
  onClose: () => void
  onMessage: (message: string) => void
}) {
  const queryClient = useQueryClient()
  const [editing, setEditing] = useState<WorkingItem | null>(item)
  const [form, setForm] = useState<WorkingForm>(() => item ? toWorkingForm(item) : emptyWorkingForm)
  const [conflict, setConflict] = useState<WorkingItem | null>(null)
  const [pendingAttachments, setPendingAttachments] = useState<PendingAttachment[]>([])
  const [removedAttachmentIds, setRemovedAttachmentIds] = useState<string[]>([])
  const [attachmentBusy, setAttachmentBusy] = useState(false)
  const [attachmentMessage, setAttachmentMessage] = useState('')
  const pendingRef = useRef<PendingAttachment[]>([])

  useEffect(() => {
    if (!open || !item) return
    setEditing(item)
    setForm(toWorkingForm(item))
    setConflict(null)
    setRemovedAttachmentIds([])
    setAttachmentMessage('')
  }, [item, open])

  useEffect(() => { pendingRef.current = pendingAttachments }, [pendingAttachments])
  useEffect(() => () => pendingRef.current.forEach((pending) => URL.revokeObjectURL(pending.previewUrl)), [])

  const attachmentsQuery = useQuery({
    queryKey: ['working-attachments', editing?.id],
    queryFn: () => api.workingAttachments(editing!.id),
    enabled: open && Boolean(editing?.id),
  })

  const cleanupAndClose = () => {
    setPendingAttachments((current) => {
      current.forEach((pending) => URL.revokeObjectURL(pending.previewUrl))
      return []
    })
    setRemovedAttachmentIds([])
    setAttachmentBusy(false)
    setAttachmentMessage('')
    setConflict(null)
    onClose()
  }

  const queueAttachments = async (files: File[]) => {
    const existingCount = (attachmentsQuery.data?.length ?? 0) - removedAttachmentIds.length
    const available = IMAGE_LIMITS.maxAttachments - existingCount - pendingAttachments.length
    if (available <= 0) {
      setAttachmentMessage(`每個 Working Item 最多可保存 ${IMAGE_LIMITS.maxAttachments} 張圖片。`)
      return
    }
    setAttachmentBusy(true)
    setAttachmentMessage('')
    const queued: PendingAttachment[] = []
    const errors: string[] = []
    for (const file of files.slice(0, available)) {
      try {
        const compressed = await compressImageToWebp(file)
        queued.push({ ...compressed, id: crypto.randomUUID(), previewUrl: URL.createObjectURL(compressed.file) })
      } catch (error) {
        errors.push(error instanceof Error ? error.message : '圖片壓縮失敗。')
      }
    }
    setPendingAttachments((current) => [...current, ...queued])
    if (files.length > available) errors.push(`已達 ${IMAGE_LIMITS.maxAttachments} 張上限，其餘圖片未加入。`)
    setAttachmentMessage(errors[0] ?? (queued.length ? `已將 ${queued.length} 張圖片轉成 WebP，儲存時會上傳。` : ''))
    setAttachmentBusy(false)
  }

  const save = useMutation({
    mutationFn: async () => {
      if (!editing) throw new Error('找不到這筆 Working Item。')
      const saved = await api.updateEntity<WorkingItem>(
        'working-items',
        editing.id,
        editing.version,
        enforceWorkingItemCompletion(form, editing),
      )
      const failedPendingIds: string[] = []
      const failedDeleteIds: string[] = []
      let uploadedCount = 0
      let deletedCount = 0
      for (const attachmentId of removedAttachmentIds) {
        try { await api.deleteWorkingAttachment(saved.id, attachmentId); deletedCount += 1 } catch { failedDeleteIds.push(attachmentId) }
      }
      for (const pending of pendingAttachments) {
        try { await api.uploadWorkingAttachment(saved.id, pending); uploadedCount += 1 } catch { failedPendingIds.push(pending.id) }
      }
      const startingCount = attachmentsQuery.data?.length ?? editing.attachmentCount ?? 0
      return {
        saved: { ...saved, attachmentCount: Math.max(0, startingCount - deletedCount + uploadedCount) },
        failedPendingIds,
        failedDeleteIds,
      }
    },
    onSuccess: ({ saved, failedPendingIds, failedDeleteIds }) => {
      queryClient.setQueryData<DashboardData>(['dashboard'], (old) => {
        if (!old) return old
        const linkedReadinessIds = new Set(saved.readinessItemIds)
        return {
          ...old,
          workingItems: old.workingItems.map((workingItem) => workingItem.id === saved.id ? saved : workingItem),
          readiness: old.readiness.map((readinessItem) => ({
            ...readinessItem,
            workingItemIds: linkedReadinessIds.has(readinessItem.id)
              ? [...new Set([...(readinessItem.workingItemIds ?? []), saved.id])]
              : (readinessItem.workingItemIds ?? []).filter((workingItemId) => workingItemId !== saved.id),
          })),
        }
      })
      void queryClient.invalidateQueries({ queryKey: ['working-attachments', saved.id] })
      void queryClient.invalidateQueries({ queryKey: ['audit', 'working-item', saved.id] })
      if (failedPendingIds.length || failedDeleteIds.length) {
        setEditing(saved)
        setForm(toWorkingForm(saved))
        setPendingAttachments((current) => current.filter((pending) => {
          if (failedPendingIds.includes(pending.id)) return true
          URL.revokeObjectURL(pending.previewUrl)
          return false
        }))
        setRemovedAttachmentIds(failedDeleteIds)
        setAttachmentMessage(`工作項目已儲存，但有 ${failedPendingIds.length + failedDeleteIds.length} 張圖片處理失敗，請再儲存一次重試。`)
        return
      }
      onMessage('Working Item 已儲存。')
      cleanupAndClose()
    },
    onError: (error) => {
      const latest = conflictCurrent<WorkingItem>(error)
      latest ? setConflict(latest) : onMessage(error instanceof Error ? error.message : '儲存失敗。')
    },
  })

  const index = editing ? data.workingItems.findIndex((workingItem) => workingItem.id === editing.id) : -1
  const itemNumber = `#${String(Math.max(0, index) + 1).padStart(2, '0')}`

  return (
    <>
      <Drawer
        open={open && Boolean(editing)}
        title={`編輯工作任務 · ${itemNumber}`}
        subtitle={editing ? `版本 ${editing.version} · 完整異動請查看下方修改紀錄` : ''}
        onClose={cleanupAndClose}
        footer={<><Button onClick={cleanupAndClose}>取消</Button><Button variant="primary" type="submit" form="readiness-working-form" disabled={save.isPending || attachmentBusy}>{save.isPending ? '儲存中…' : attachmentBusy ? '圖片處理中…' : '儲存'}</Button></>}
      >
        {editing ? (
          <WorkingFormView
            form={form}
            setForm={setForm}
            projects={data.projects}
            readinessItems={data.readiness}
            itemNumber={itemNumber}
            workingItemId={editing.id}
            existingAttachments={(attachmentsQuery.data ?? []).filter((attachment: WorkingAttachment) => !removedAttachmentIds.includes(attachment.id))}
            pendingAttachments={pendingAttachments}
            removedAttachmentCount={removedAttachmentIds.length}
            attachmentsLoading={attachmentsQuery.isLoading}
            attachmentBusy={attachmentBusy}
            attachmentMessage={attachmentMessage}
            onQueueAttachments={(files) => { void queueAttachments(files) }}
            onRemoveExisting={(id) => setRemovedAttachmentIds((current) => [...current, id])}
            onRemovePending={(id) => setPendingAttachments((current) => current.filter((pending) => {
              if (pending.id !== id) return true
              URL.revokeObjectURL(pending.previewUrl)
              return false
            }))}
            onSubmit={(event) => { event.preventDefault(); save.mutate() }}
            formId="readiness-working-form"
          />
        ) : null}
        <HistoryPanel entity="working-item" id={editing?.id} />
      </Drawer>
      <ConflictDialog
        open={Boolean(conflict)}
        local={form as unknown as Record<string, unknown>}
        remote={(conflict ?? {}) as unknown as Record<string, unknown>}
        labels={conflictLabels}
        onClose={() => setConflict(null)}
        onUseLatest={() => {
          if (!conflict) return
          setEditing(conflict)
          setForm(toWorkingForm(conflict))
          setConflict(null)
        }}
      />
    </>
  )
}
